import isNil from 'lodash/isNil';
import keyBy from 'lodash/keyBy';

import AuthActionTypes from 'store/modules/auth/constants';
import { ApplicationActions } from 'store/types';
import { normalisePlateNumberSearch } from 'utils/integration/normalise-plate-number';
import { upsertSafeChildProperties } from 'utils/Object/properties/upsert-child-property';

import ActionTypes from './constants';
import { IPCamerasState, PlateNumber } from './types';

export const initialState: IPCamerasState = {};

function ipCamerasReducer(
  state: IPCamerasState = initialState,
  action: ApplicationActions
): IPCamerasState {
  switch (action.type) {
    case ActionTypes.FETCH_IP_CAMERAS_SUCCESS: {
      const { payload } = action;
      return Object.entries(payload).reduce<IPCamerasState>(
        (acc, [idKey, ipCamera]) => upsertSafeChildProperties(acc, [Number(idKey)], ipCamera),
        { ...state }
      );
    }
    case ActionTypes.FETCH_CAMERA_BY_ID_SUCCESS: {
      const { payload } = action;
      return upsertSafeChildProperties(state, [payload.id], payload);
    }
    case ActionTypes.SET_IP_CAMERA_PREVIEW: {
      const { payload } = action;
      return {
        ...state,
        [payload.ipCameraId]: {
          ...state[payload.ipCameraId],
          preview: payload.preview,
          errorRefreshingPreview: undefined
        }
      };
    }
    case ActionTypes.SET_IP_CAMERA_PREVIEW_ERROR: {
      const { payload } = action;
      return {
        ...state,
        [payload.ipCameraId]: {
          ...state[payload.ipCameraId],
          errorRefreshingPreview: payload.error
        }
      };
    }
    case ActionTypes.SET_IP_CAMERA_STATE: {
      const { payload } = action;
      return {
        ...state,
        [payload.ipCameraId]: {
          ...state[payload.ipCameraId],
          metadata: payload.metadata
        }
      };
    }
    case ActionTypes.SET_IP_CAMERA_CAPABILITIES: {
      const { payload } = action;
      const existing = state[payload.ipCameraId];
      const previousCapabilities = existing?.capabilities ?? {};
      const capabilities = {
        ...previousCapabilities,
        ...payload.partialCapabilities,
        ...(!isNil(payload.channels) && { channels: payload.channels })
      };
      return {
        ...state,
        [payload.ipCameraId]: {
          ...existing,
          capabilities
        }
      };
    }
    case ActionTypes.SET_IP_CAMERA_PHOTOS: {
      const { payload } = action;
      return {
        ...state,
        [payload.ipCameraId]: {
          ...state[payload.ipCameraId],
          photoList: payload.photoList
          // TODO: delete this line once we have more images in BE to test for pagination
          // photoList: {
          //   ...payload.photoList,
          //   currentData: mockIPCameraPhotos,
          //   totalDataItems: mockIPCameraPhotos.length
          // }
        }
      };
    }
    case ActionTypes.SET_IP_CAMERA_UNSEEN_PHOTO: {
      const { payload } = action;
      return {
        ...state,
        [payload.ipCameraId]: {
          ...state[payload.ipCameraId],
          unseenNewPhoto: payload.newPhoto
        }
      };
    }
    case ActionTypes.SET_IP_CAMERA_EVENTS: {
      const { payload } = action;
      return {
        ...state,
        [payload.ipCameraId]: {
          ...state[payload.ipCameraId],
          eventList: payload.events
        }
      };
    }
    case ActionTypes.REMOVE_FROM_IP_CAMERA_LIST: {
      const { payload: { id } } = action;
      const newState = { ...state };
      delete newState[id];
      return newState;
    }
    case ActionTypes.SET_IP_CAMERA_PTZ_PRESETS: {
      const { payload: { ipCameraId, presets } } = action;
      return {
        ...state,
        [ipCameraId]: {
          ...state[ipCameraId],
          ptzPresets: keyBy(presets, 'id')
        }
      };
    }
    case ActionTypes.SET_IP_CAMERA_PTZ_PRESET: {
      const { payload: { ipCameraId, preset } } = action;

      return {
        ...state,
        [ipCameraId]: {
          ...state[ipCameraId],
          ptzPresets: {
            ...state[ipCameraId]?.ptzPresets,
            [preset.id]: preset
          }
        }
      };
    }
    case ActionTypes.REMOVE_IP_CAMERA_PTZ_PRESET: {
      const { payload: { ipCameraId, presetId } } = action;
      const presets = state[ipCameraId].ptzPresets;

      if (presets) {
        delete presets[presetId];
      }

      return {
        ...state,
        [ipCameraId]: {
          ...state[ipCameraId],
          ptzPresets: presets
        }
      };
    }
    case ActionTypes.SET_ALLOWED_PLATE_NUMBERS: {
      const { payload } = action;
      const existing = state[payload.ipCameraId];
      const existingAllowed = existing?.allowedPlateNumbers;
      // Drop a stale append whose search context no longer matches the current list.
      if (
        payload.isAppend
        && normalisePlateNumberSearch(payload.search)
        !== normalisePlateNumberSearch(existingAllowed?.search)
      ) {
        return state;
      }
      let plateNumbers: PlateNumber[];
      let incoming = payload.plateNumbers;
      if (payload.isAppend) {
        const current = existingAllowed?.currentData ?? [];
        const currentSet = new Set(current);
        // Dedupe across the page boundary: optimistic inserts (or server-side reordering)
        // can shift offsets so a page overlaps with what is already loaded.
        incoming = payload.plateNumbers.filter((plateNumber) => !currentSet.has(plateNumber));
        plateNumbers = [...current, ...incoming];
      } else {
        plateNumbers = payload.plateNumbers;
      }
      // Search responses return a filtered count; keep totalDataItems as the unfiltered total
      // for the header subtext.
      const search = payload.isAppend
        ? existingAllowed?.search ?? ''
        : payload.search ?? '';
      const hasActiveSearch = !!normalisePlateNumberSearch(search);
      const totalDataItems = hasActiveSearch
        ? existingAllowed?.totalDataItems ?? payload.total
        : payload.total;
      const hasMore = payload.isAppend && incoming.length === 0
        ? false
        : payload.page < payload.totalPages;
      return {
        ...state,
        [payload.ipCameraId]: {
          ...existing,
          allowedPlateNumbers: {
            ...existingAllowed,
            currentData: plateNumbers,
            totalDataItems,
            currentPage: payload.page,
            hasMore,
            // Track the search used for the latest non-append fetch so add/delete refetches stay consistent
            search
          }
        }
      };
    }
    case ActionTypes.SET_ALLOWED_PLATE_NUMBERS_SEARCH: {
      const { payload } = action;
      const existing = state[payload.ipCameraId];
      const existingAllowed = existing?.allowedPlateNumbers;
      if (isNil(existingAllowed)) {
        return state;
      }
      return {
        ...state,
        [payload.ipCameraId]: {
          ...existing,
          allowedPlateNumbers: {
            ...existingAllowed,
            search: payload.search
          }
        }
      };
    }
    case ActionTypes.PREPEND_ALLOWED_PLATE_NUMBERS: {
      const { payload } = action;
      const existing = state[payload.ipCameraId];
      const existingAllowed = existing?.allowedPlateNumbers;
      if (isNil(existingAllowed)) {
        return state;
      }
      const current = existingAllowed.currentData;
      const currentSet = new Set(current);
      const newPlateNumbers = payload.plateNumbers.filter((plateNumber) => !currentSet.has(plateNumber));
      const plateNumbers = [...newPlateNumbers, ...current];
      // Increment by the server's reported added count, not the prepended count, so the
      // header total stays correct even if some added plates live on not-yet-loaded pages.
      const total = existingAllowed.totalDataItems + payload.addedTotal;
      return {
        ...state,
        [payload.ipCameraId]: {
          ...existing,
          allowedPlateNumbers: {
            ...existingAllowed,
            currentData: plateNumbers,
            totalDataItems: total,
            // Pagination exhaustion is determined by fetch responses (page vs totalPages),
            // not by comparing loaded count to total — prepending must not re-enable Load More.
            hasMore: existingAllowed.hasMore
          }
        }
      };
    }
    case ActionTypes.REMOVE_ALLOWED_PLATE_NUMBERS_FROM_LIST: {
      const { payload } = action;
      const existing = state[payload.ipCameraId];
      const existingAllowed = existing?.allowedPlateNumbers;
      if (isNil(existingAllowed)) {
        return state;
      }
      const toRemove = new Set(payload.plateNumbers);
      const previousPlateNumbers = existingAllowed.currentData;
      const plateNumbers = previousPlateNumbers.filter((plateNumber) => !toRemove.has(plateNumber));
      const removedCount = previousPlateNumbers.length - plateNumbers.length;
      const total = Math.max(existingAllowed.totalDataItems - removedCount, 0);
      return {
        ...state,
        [payload.ipCameraId]: {
          ...existing,
          allowedPlateNumbers: {
            ...existingAllowed,
            currentData: plateNumbers,
            totalDataItems: total,
            // Deleting a loaded plate lowers both counts equally; keep the fetch-derived
            // hasMore flag so Load More does not reappear after pagination was exhausted.
            hasMore: existingAllowed.hasMore
          }
        }
      };
    }
    case AuthActionTypes.LOGOUT:
      return initialState;
    default:
      return state;
  }
}

export default ipCamerasReducer;